import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import UserSearchAutocomplete from '../components/users/UserSearchAutocomplete';
import type { AdminUserRow } from '../services/usersApi';
import { ContentPageLayout } from '../components/Shared';
import {
  ProgressionLocaleFields,
  emptyProgressionLocales,
  progressionLocalesFromEntity,
  progressionPayloadFromLocales,
  type ProgressionLocalesForm,
} from '../components/ProgressionLocaleFields';
import {
  addLevel,
  assignEnrolment,
  deleteLevel,
  getFormation,
  listEnrolments,
  publishFormation,
  reorderLevels,
  updateFormation,
  validateFormation,
  type FormationTree,
  type TrainingEnrolmentRow,
  type TrainingLevel,
} from '../services/formationsAdminApi';

const FormationEditorPage: React.FC = () => {
  const { formationId } = useParams<{ formationId: string }>();
  const { t } = useTranslation();
  const { navigateWithLang } = useLanguage();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [tree, setTree] = useState<FormationTree | null>(null);
  const [locales, setLocales] = useState<ProgressionLocalesForm>(emptyProgressionLocales());
  const [code, setCode] = useState('');
  const [newLevelCode, setNewLevelCode] = useState('');
  const [newLevelTitle, setNewLevelTitle] = useState('');
  const [validateErrors, setValidateErrors] = useState<string[]>([]);
  const [enrolments, setEnrolments] = useState<TrainingEnrolmentRow[]>([]);
  const [assignTeacher, setAssignTeacher] = useState<AdminUserRow | null>(null);

  const load = useCallback(async () => {
    if (!formationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getFormation(formationId);
      const data = res.data;
      if (!data) throw new Error('Formation not found');
      setTree(data);
      setCode(data.program.code || '');
      setLocales(
        progressionLocalesFromEntity({
          name: data.program.locales?.en?.title,
          description: data.program.locales?.en?.shortDescription,
          locales: {
            en: {
              name: data.program.locales?.en?.title || '',
              description: data.program.locales?.en?.shortDescription || '',
            },
            fr: {
              name: data.program.locales?.fr?.title || '',
              description: data.program.locales?.fr?.shortDescription || '',
            },
          },
        }),
      );
      if (formationId) {
        const enrolRes = await listEnrolments(formationId);
        setEnrolments(enrolRes.data?.enrolments || []);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load formation');
    } finally {
      setLoading(false);
    }
  }, [formationId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveGeneral = async () => {
    if (!formationId) return;
    setSaving(true);
    setMsg(null);
    setError(null);
    try {
      const payload = progressionPayloadFromLocales(locales);
      await updateFormation(formationId, {
        code,
        locales: {
          en: { title: payload.locales.en.name, shortDescription: payload.locales.en.description },
          fr: { title: payload.locales.fr.name, shortDescription: payload.locales.fr.description },
        },
      });
      setMsg(t('formations.saved', { defaultValue: 'Saved' }));
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAddLevel = async () => {
    if (!formationId || !newLevelCode.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await addLevel(formationId, {
        code: newLevelCode.trim(),
        locales: {
          en: { title: newLevelTitle.trim() || newLevelCode.trim() },
          fr: { title: newLevelTitle.trim() || newLevelCode.trim() },
        },
      });
      setNewLevelCode('');
      setNewLevelTitle('');
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to add level');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLevel = async (level: TrainingLevel) => {
    if (!window.confirm(t('formations.level.deleteConfirm', { defaultValue: 'Delete this level and its lessons?' }))) {
      return;
    }
    setSaving(true);
    try {
      await deleteLevel(level._id);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const sortedLevels = [...(tree?.levels || [])].sort((a, b) => a.order - b.order);

  const moveLevel = async (index: number, direction: -1 | 1) => {
    if (!formationId || !tree) return;
    const levels = [...sortedLevels];
    const target = index + direction;
    if (target < 0 || target >= levels.length) return;
    [levels[index], levels[target]] = [levels[target], levels[index]];
    const orderedIds = levels.map((level) => level._id);
    setSaving(true);
    setError(null);
    try {
      await reorderLevels(formationId, orderedIds);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Reorder failed');
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    if (!formationId) return;
    setSaving(true);
    setValidateErrors([]);
    try {
      const res = await validateFormation(formationId);
      const data = res.data;
      if (!data?.valid) {
        setValidateErrors(data?.errors || []);
        setError(t('formations.publish.invalid', { defaultValue: 'Validation failed' }));
      } else {
        setMsg(t('formations.publish.valid', { defaultValue: 'Ready to publish' }));
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!formationId) return;
    setSaving(true);
    setError(null);
    try {
      await publishFormation(formationId);
      setMsg(t('formations.publish.done', { defaultValue: 'Formation published' }));
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Publish failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAssign = async () => {
    if (!formationId || !assignTeacher?._id) return;
    setSaving(true);
    setError(null);
    try {
      await assignEnrolment(formationId, assignTeacher._id);
      setAssignTeacher(null);
      setMsg(t('formations.enrolments.assigned', { defaultValue: 'Teacher assigned' }));
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Assign failed');
    } finally {
      setSaving(false);
    }
  };

  const enrolledUserIds = new Set(enrolments.map((row) => String(row.userId)));

  if (loading) {
    return (
      <ContentPageLayout>
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      </ContentPageLayout>
    );
  }

  if (!tree) {
    return (
      <ContentPageLayout>
        <Alert severity="error">{error || 'Formation not found'}</Alert>
      </ContentPageLayout>
    );
  }

  return (
    <ContentPageLayout>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigateWithLang('/formations')} sx={{ mb: 2 }}>
        {t('formations.back', { defaultValue: 'Back to list' })}
      </Button>

      <Typography variant="h5" gutterBottom sx={{'color': 'primary.main','fontWeight': 'bold'}}>
        {tree.program.code}
        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
          ({tree.program.status})
        </Typography>
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {msg ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert> : null}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={t('formations.tabs.general', { defaultValue: 'General' })} />
        <Tab label={t('formations.tabs.levels', { defaultValue: 'Levels' })} />
        <Tab label={t('formations.tabs.publish', { defaultValue: 'Publish' })} />
        <Tab label={t('formations.tabs.enrolments', { defaultValue: 'Enrolments' })} />
      </Tabs>

      {tab === 0 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            label={t('formations.fields.code', { defaultValue: 'Code' })}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            sx={{ mb: 2 }}
          />
          <ProgressionLocaleFields
            locales={locales}
            onChange={setLocales}
            nameLabel={t('formations.fields.title', { defaultValue: 'Title' })}
            descriptionLabel={t('formations.fields.shortDescription', { defaultValue: 'Short description' })}
          />
          <Box mt={2}>
            <Button variant="contained" onClick={saveGeneral} disabled={saving}>
              {t('formations.save', { defaultValue: 'Save' })}
            </Button>
          </Box>
        </Paper>
      ) : tab === 1 ? (
        <Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              {t('formations.level.add', { defaultValue: 'Add level' })}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <TextField size="small" label="Code" value={newLevelCode} onChange={(e) => setNewLevelCode(e.target.value)} />
              <TextField size="small" label="Title" value={newLevelTitle} onChange={(e) => setNewLevelTitle(e.target.value)} />
              <Button variant="contained" startIcon={<AddIcon />} disabled={saving || !newLevelCode.trim()} onClick={handleAddLevel}>
                {t('formations.level.addButton', { defaultValue: 'Add' })}
              </Button>
            </Box>
          </Paper>

          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>#</TableCell>
                  <TableCell sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>{t('formations.fields.code', { defaultValue: 'Code' })}</TableCell>
                  <TableCell sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>{t('formations.fields.title', { defaultValue: 'Title' })}</TableCell>
                  <TableCell align="right" sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>{t('formations.fields.lessons', { defaultValue: 'Lessons' })}</TableCell>
                  <TableCell align="right" sx={{'color': 'primary.main', 'fontWeight': 'bold'}} />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedLevels.map((level, index) => (
                  <TableRow key={level._id} hover>
                    <TableCell>{level.order}</TableCell>
                    <TableCell>{level.code}</TableCell>
                    <TableCell>{level.locales?.fr?.title || level.locales?.en?.title}</TableCell>
                    <TableCell align="right">{level.lessons?.length ?? 0}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton
                        size="small"
                        disabled={saving || index === 0}
                        onClick={() => moveLevel(index, -1)}
                        aria-label={t('formations.level.moveUp', { defaultValue: 'Move level up' })}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={saving || index === sortedLevels.length - 1}
                        onClick={() => moveLevel(index, 1)}
                        aria-label={t('formations.level.moveDown', { defaultValue: 'Move level down' })}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigateWithLang(`/formations/${formationId}/levels/${level._id}`)}
                        aria-label={t('formations.level.edit', { defaultValue: 'Edit' })}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteLevel(level)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      ) : tab === 2 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('formations.publish.help', {
              defaultValue: 'Validate the curriculum, then publish to create an immutable snapshot for enrolments.',
            })}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {t('formations.publish.version', { defaultValue: 'Version' })}: {tree.program.version ?? 0}
          </Typography>
          {validateErrors.length ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validateErrors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          <Box display="flex" gap={1}>
            <Button variant="outlined" onClick={handleValidate} disabled={saving}>
              {t('formations.publish.validate', { defaultValue: 'Validate' })}
            </Button>
            <Button variant="contained" onClick={handlePublish} disabled={saving || tree.program.status === 'archived'}>
              {t('formations.publish.button', { defaultValue: 'Publish' })}
            </Button>
          </Box>
        </Paper>
      ) : (
        <Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('formations.enrolments.assign', { defaultValue: 'Assign teacher' })}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <UserSearchAutocomplete
                value={assignTeacher}
                onChange={setAssignTeacher}
                roleFilter="teacher"
                excludeUserIds={[...enrolledUserIds]}
                disabled={saving}
                label={t('formations.enrolments.searchTeacher', { defaultValue: 'Teacher' })}
                placeholder={t('formations.enrolments.searchPlaceholder', {
                  defaultValue: 'Search by name or email',
                })}
                sx={{ minWidth: 320, maxWidth: 480 }}
              />
              <Button
                variant="contained"
                disabled={saving || !assignTeacher?._id}
                onClick={handleAssign}
              >
                {t('formations.enrolments.assignButton', { defaultValue: 'Assign' })}
              </Button>
            </Box>
          </Paper>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{'color': 'primary.main'}}>{t('formations.enrolments.user', { defaultValue: 'User' })}</TableCell>
                  <TableCell sx={{'color': 'primary.main'}}>{t('formations.enrolments.status', { defaultValue: 'Status' })}</TableCell>
                  <TableCell align="right" sx={{'color': 'primary.main'}}>{t('formations.enrolments.progress', { defaultValue: 'Progress' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enrolments.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell>{row.user?.nickname || row.user?.email || row.userId}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell align="right">{row.percentComplete ?? 0}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}
    </ContentPageLayout>
  );
};

export default FormationEditorPage;
